import type { ReactNode } from "react";
import ReactSelect from "react-select";
import type { Props as ReactSelectProps, StylesConfig } from "react-select";
import styles from "./Select.module.scss";
import withLabelAndError from "../withLabelAndError";
import { Controller } from "react-hook-form";
import type { Control, RegisterOptions } from "react-hook-form";
import { combineClasses } from "@/lib/utils";
import LoadingText from "@/components/LoadingText/LoadingText";

interface SelectOption {
  label: ReactNode;
  value: string;
}

export interface SelectProps extends ReactSelectProps<SelectOption, boolean> {
  control?: Control<any>;
  validations?: RegisterOptions;
  name: string;
  optionsHeight?: number;
  className?: string;
  loadingText?: string;
  isLoading?: boolean;
  label?: string;
  labelVariant?: "default" | "caption";
  // withLabelAndError folds `isDisabled` into `disabled` before rendering
  // this control, so both must be honoured here.
  disabled?: boolean | undefined;
}

const Select = (props: SelectProps) => {
  const {
    control,
    validations,
    name,
    placeholder,
    isDisabled,
    disabled,
    options,
    optionsHeight,
    isMulti,
    className = "",
    loadingText = "Loading",
    isLoading = false,
    formatOptionLabel,
    ...rest
  } = props;

  const loadingOption: SelectOption = {
    label: <LoadingText text={loadingText} style={{ fontWeight: "400" }} />,
    value: "loading",
  };

  const customStyles: StylesConfig<SelectOption, boolean> = {
    control: (provided) => ({
      ...provided,
      minHeight: "40px",
      height: isMulti ? "auto" : "40px",
      background: "transparent",
      border: "none",
      boxShadow: "none",
    }),
    valueContainer: (provided) => ({
      ...provided,
      alignItems: "center",
      height: "100%",
      flexWrap: isMulti ? "wrap" : "nowrap",
    }),
    input: (provided) => ({
      ...provided,
      margin: 0,
      padding: 0,
    }),
    placeholder: (provided) => ({
      ...provided,
      margin: 0,
      lineHeight: "1.35",
      overflow: "hidden",
      textOverflow: "ellipsis",
      whiteSpace: "nowrap",
      maxWidth: "100%",
    }),
    singleValue: (provided) => ({
      ...provided,
      margin: 0,
      lineHeight: "1.35",
      overflow: "hidden",
      textOverflow: "ellipsis",
      whiteSpace: "nowrap",
      maxWidth: "100%",
    }),
    indicatorsContainer: (provided) => ({
      ...provided,
      height: "100%",
      alignItems: "center",
    }),
    dropdownIndicator: (provided) => ({
      ...provided,
      alignItems: "center",
      display: "flex",
    }),
    menu: (provided) => ({
      ...provided,
      zIndex: 9999,
    }),
    menuPortal: (provided) => ({
      ...provided,
      zIndex: 9999,
    }),
    menuList: (provided) => ({
      ...provided,
      maxHeight: optionsHeight || 200,
      overflowY: "auto",
    }),
    option: (provided, state) => ({
      ...provided,
      backgroundColor: state.isSelected
        ? "rgb(var(--primary-color))"
        : state.isFocused
          ? "#f3f4f6"
          : "transparent",
      color: state.isSelected ? "#ffffff" : "#111827",
      cursor: "pointer",
    }),
  };

  const renderInput = (field: Record<string, unknown> = {}) => (
    <ReactSelect<SelectOption, boolean>
      {...rest}
      {...field}
      id={name}
      className={combineClasses(styles.inputField, className)}
      menuPortalTarget={typeof document !== "undefined" ? document.body : null}
      menuPosition="fixed"
      value={
        isLoading
          ? loadingOption
          : field?.value !== undefined
            ? (field.value as SelectOption | null)
            : ((rest?.value as SelectOption) ?? null)
      }
      options={isLoading ? [loadingOption] : options}
      styles={customStyles}
      placeholder={placeholder}
      classNamePrefix="react-select"
      isDisabled={isDisabled || disabled || isLoading}
      isMulti={isMulti}
      getOptionValue={(option) => option.value}
      formatOptionLabel={
        formatOptionLabel ??
        ((option) => {
          if (typeof option.label === "string") return option.label;

          if (typeof option.label === "object" && option.label !== null) {
            return option.label;
          }

          return String(option.label);
        })
      }
    />
  );

  if (control) {
    return (
      <Controller
        control={control}
        {...(validations ? { rules: validations } : {})}
        name={name}
        render={({ field }) => renderInput(field)}
      />
    );
  } else {
    return renderInput();
  }
};

const SelectWithLabelAndError = withLabelAndError<SelectProps>(Select);

export default SelectWithLabelAndError;
export { Select };
